import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Stripe } from 'stripe';

import { notify } from 'src/clients/ApiService';
import {
  AppAPIName,
  SUBSCRIPTION_PAGE,
  SubscriptionStatus,
} from 'src/constants';
import history from 'src/history';
import { LineItem } from 'src/legacy/components/billing/new/types';
import { RootState } from 'src/store';
import {
  deleteSubscriptionsAction,
  updateSubscriptionsAction,
} from 'src/store/payments/actions';
import { SubscriptionEntity } from 'src/store/payments/types';
import {
  Subscription,
  SubscriptionCollectionMethod,
  SubscriptionFields,
  SubscriptionSchema,
  SubscriptionTemplateSchema,
} from 'src/types/subscriptions';
import { API } from 'src/utils/AmplifyApiUtils';
import { arrayToMap } from 'src/utils/array';
import { ensureApiError } from 'src/utils/Errors';

import { ApiMethods, ApiTags, appAPI } from '.';

export type LineItemWithRate = LineItem & {
  rate: number;
};

export type GetSubscriptionsByRecipientIdQueryParams = {
  clientId?: string;
  companyId?: string;
  recipientId?: string;
};

// API payload type that supports both legacy (with recipientId) and new (with clientUserId/companyId) formats
export type SubscriptionCreatePayload =
  | SubscriptionSchema
  | {
      fields: {
        clientUserId?: string;
        companyId?: string;
        lineItems: SubscriptionSchema['fields']['lineItems'];
        paymentPreferences: SubscriptionSchema['fields']['paymentPreferences'];
        taxPercentage?: string;
        totalAmount: number;
        attachment: string | null;
        memo?: string;
        currency: string | null;
        prorate?: boolean;
        resetBillingCycle?: boolean;
        interval: SubscriptionSchema['fields']['interval'];
        scheduleStartDate?: number;
        scheduleEndDate?: number;
        scheduleIterations?: number;
        status: SubscriptionSchema['fields']['status'];
        created?: number;
      } & (
        | {
            collectionMethod: SubscriptionCollectionMethod.SendInvoice;
            dueDate: string;
            dueDays: string;
          }
        | {
            collectionMethod: SubscriptionCollectionMethod.ChargeAutomatically;
            primarySource: string | null;
          }
      );
    };

type SubscriptionMetaData = Pick<
  SubscriptionFields,
  'taxPercentage' | 'memo' | 'interval' | 'attachment'
> & {
  lineItems: LineItemWithRate[];
};

export type SubscriptionTemplate = {
  id: string;
  additionalFields: {
    templateName: string;
  };
  fields: SubscriptionMetaData;
};

type SubscriptionFieldsWithRateLineItem = Omit<
  SubscriptionFields,
  'lineItems'
> & {
  lineItems: LineItemWithRate[];
};

export type SubscriptionWithRateLineItem = Omit<Subscription, 'fields'> & {
  fields: SubscriptionFieldsWithRateLineItem;
};

type UpdateSubscriptionTemplatePayload = SubscriptionTemplateSchema & {
  id: string;
};

type ListSubscriptionResponse = {
  data: Subscription[];
  nextToken?: string;
};

async function loadSubscriptions(
  previousItems: Subscription[],
  nextToken?: string,
  recipientId?: string,
  clientId?: string,
  companyId?: string,
): Promise<Subscription[]> {
  const url = '/subscriptions';

  const response: ListSubscriptionResponse = await API.get(AppAPIName, url, {
    queryStringParameters: {
      limit: 1000,
      nextToken,
      ...(recipientId && { recipientId }),
      ...(clientId && { clientId }),
      ...(companyId && { companyId }),
    },
  });

  const responseItems = response?.data ? response.data : [];
  const updatedItems = [...previousItems, ...responseItems];

  if (!response.nextToken) {
    return updatedItems;
  }

  return loadSubscriptions(
    updatedItems,
    response.nextToken,
    recipientId,
    clientId,
    companyId,
  );
}

type UpdateSubscriptionPayload = SubscriptionCreatePayload & {
  id: string;
};

export const subscriptionsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getSubscriptionEntityById: build.query<
      SubscriptionEntity,
      Pick<SubscriptionEntity, 'id'>
    >({
      query: ({ id }) => ({
        path: `/v0/subscriptions/entity/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
    }),
    getNextInvoiceSummary: build.query<Stripe.Invoice, any>({
      query: ({ id, ...subscription }) => ({
        path: `/subscriptions/${id}/next-invoice`,
        method: ApiMethods.post,
        options: { body: subscription },
      }),
    }),
    markSubscriptionDeleted: build.mutation<
      void,
      { id: string; subscriptions: SubscriptionEntity[] }
    >({
      query: ({ id }) => ({
        path: `/v0/subscriptions/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      onQueryStarted({ subscriptions, id }, { dispatch, queryFulfilled }) {
        const filteredSubscription = subscriptions.filter(
          (sub: SubscriptionEntity) => sub.id === id,
        );
        dispatch(deleteSubscriptionsAction(filteredSubscription));
        queryFulfilled.catch(() =>
          dispatch(updateSubscriptionsAction(subscriptions)),
        );
      },
    }),
    getSubscriptionById: build.query<SubscriptionWithRateLineItem, string>({
      query: (id) => ({
        path: `/subscriptions/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
    }),
    getSubscriptions: build.query<Subscription[], string | undefined>({
      queryFn: async (recipientId) => {
        const response = await loadSubscriptions([], undefined, recipientId);
        return {
          data: response ?? [],
        };
      },
      providesTags: [ApiTags.subscriptions],
    }),

    getSubscriptionsByRecipientId: build.query<
      Subscription[],
      GetSubscriptionsByRecipientIdQueryParams
    >({
      queryFn: async ({ clientId, companyId, recipientId }) => {
        const response = await loadSubscriptions(
          [],
          undefined,
          recipientId,
          clientId,
          companyId,
        );
        const sortedSubscriptions = (response || []).sort((a, b) => {
          // First sort by status (Active first, then Cancelled)
          if (a.additionalFields?.status !== b.additionalFields?.status) {
            return a.additionalFields?.status === SubscriptionStatus.Active
              ? -1
              : 1;
          }
          // Then sort by creation date (newest first)
          return b.createdAt.localeCompare(a.createdAt);
        });
        return { data: sortedSubscriptions };
      },
      providesTags: [ApiTags.clientSubscription],
    }),
    createSubscription: build.mutation<Subscription, SubscriptionCreatePayload>(
      {
        query: (subscription) => ({
          path: '/subscriptions',
          method: ApiMethods.post,
          options: {
            body: {
              ...subscription,
              fields: {
                ...subscription.fields,
                taxPercentage: Number(subscription.fields.taxPercentage),
                daysUntilDue:
                  subscription.fields.collectionMethod ===
                  SubscriptionCollectionMethod.SendInvoice
                    ? Number(subscription.fields.dueDays)
                    : null,
                lineItems: subscription.fields.lineItems.map((item) => ({
                  ...item,
                  rate: Number(item.amount),
                  quantity: Number(item.quantity),
                })),
              },
            },
          },
        }),
        invalidatesTags: [ApiTags.subscriptions],
        async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
          try {
            const result = await queryFulfilled;
            const subscription = result.data;
            const state = getState() as RootState;

            updateSubscriptionQuery(subscription, dispatch, state);

            notify({
              status: 'success',
              successMessage: 'Subscription is created successfully.',
              dispatch,
            });
          } catch (err) {
            const error = ensureApiError(err);
            const errorMessage = error.message;
            notify({
              status: 'error',
              errorMessage:
                errorMessage || 'Subscription could not be created.',
              error,
              dispatch,
            });
          }
        },
      },
    ),
    updateSubscription: build.mutation<Subscription, UpdateSubscriptionPayload>(
      {
        query: (subscription) => ({
          path: `/subscriptions/${subscription.id}`,
          method: ApiMethods.put,
          options: {
            body: {
              ...subscription,
              fields: {
                ...subscription.fields,
                taxPercentage: Number(subscription.fields.taxPercentage),
                daysUntilDue:
                  subscription.fields.collectionMethod ===
                  SubscriptionCollectionMethod.SendInvoice
                    ? Number(subscription.fields.dueDays)
                    : null,
                lineItems: subscription.fields.lineItems.map((item) => ({
                  ...item,
                  rate: Number(item.amount),
                  quantity: Number(item.quantity),
                })),
              },
            },
          },
        }),
        invalidatesTags: [ApiTags.subscriptions],
        async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
          try {
            const result = await queryFulfilled;
            const subscription = result.data;
            const state = getState() as RootState;

            updateSubscriptionQuery(subscription, dispatch, state);

            dispatch(
              subscriptionsApi.util.updateQueryData(
                'getSubscriptionById',
                subscription.id,
                (draft) => {
                  draft.fields = subscription.fields;
                  draft.paymentSourceId = subscription.paymentSourceId;
                },
              ),
            );

            notify({
              status: 'success',
              successMessage: 'Subscription is updated successfully.',
              dispatch,
            });
          } catch (err) {
            const error = ensureApiError(err);
            const errorMessage = error.message;
            notify({
              status: 'error',
              errorMessage:
                errorMessage || 'Subscription could not be updated.',
              error,
              dispatch,
            });
          }
        },
      },
    ),
    deleteSubscription: build.mutation<
      void,
      { id: string; clientUserId: string; companyId: string }
    >({
      query: (params) => ({
        path: `/subscriptions/${params.id}`,
        method: ApiMethods.del,
        options: {},
      }),
      invalidatesTags: [ApiTags.subscriptions],
      async onQueryStarted(param, { dispatch, queryFulfilled, getState }) {
        const state = getState() as unknown as RootState;
        let clientSubscriptionPatch;
        const { clientUserId, companyId, id: subscriptionId } = param;

        const { companies, clients } = state.clients;
        if (companyId) {
          const companiesMap = arrayToMap(companies, 'id');
          const company = companiesMap.get(companyId);
          clientSubscriptionPatch = deleteSubscriptionFromCache(
            dispatch,
            'getSubscriptionsByRecipientId',
            companyId,
            param.id,
          );

          if (company) {
            const companyClientsIds = clients
              .filter(
                (client) =>
                  // TODO:(enable_many_companies) remove the companyId check once we enable many companies
                  client.fields.companyId === companyId ||
                  client.fields.companyIds?.includes(companyId),
              )
              .map((client) => client.id);
            for (const clientId of companyClientsIds) {
              clientSubscriptionPatch = deleteSubscriptionFromCache(
                dispatch,
                'getSubscriptionsByRecipientId',
                clientId,
                subscriptionId,
              );
            }
          } else {
            clientSubscriptionPatch = deleteSubscriptionFromCache(
              dispatch,
              'getSubscriptionsByRecipientId',
              clientUserId,
              subscriptionId,
            );
          }
        }

        const patchResult = deleteSubscriptionFromCache(
          dispatch,
          'getSubscriptions',
          undefined,
          subscriptionId,
        );

        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Subscription is deleted successfully.',
            dispatch,
          });
        } catch (err) {
          patchResult.undo();
          clientSubscriptionPatch?.undo();
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Subscription could not be deleted.',
            error,
            dispatch,
          });
        }
      },
    }),
    cancelSubscription: build.mutation<void, Subscription>({
      query: (param) => ({
        path: `/subscriptions/${param.id}/cancel`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: [ApiTags.subscriptions],
      async onQueryStarted(param, { dispatch, queryFulfilled, getState }) {
        let clientSubscriptionPatch;
        const subscriptionId = param.id;
        const { companyId, clientUserId } = param.fields;
        const state = getState() as unknown as RootState;
        const { companies, clients } = state.clients;
        if (companyId) {
          const companiesMap = arrayToMap(companies, 'id');
          // If found, this indicates that this is a company subscription
          const company = companiesMap.get(companyId);

          clientSubscriptionPatch = cancelSubscriptionInCache(
            dispatch,
            'getSubscriptionsByRecipientId',
            companyId,
            subscriptionId,
          );

          if (company) {
            // For company subscription, we need to update the client details page for all client for this company
            const companyClientsIds = clients
              .filter((client) => client.fields.companyId === companyId)
              .map((client) => client.id);
            for (const clientId of companyClientsIds) {
              clientSubscriptionPatch = cancelSubscriptionInCache(
                dispatch,
                'getSubscriptionsByRecipientId',
                clientId,
                subscriptionId,
              );
            }
          }
        }
        if (clientUserId) {
          clientSubscriptionPatch = cancelSubscriptionInCache(
            dispatch,
            'getSubscriptionsByRecipientId',
            clientUserId,
            subscriptionId,
          );
        }
        const patchResult = cancelSubscriptionInCache(
          dispatch,
          'getSubscriptions',
          undefined,
          subscriptionId,
        );

        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Subscription is cancelled successfully.',
            dispatch,
          });
        } catch (err) {
          patchResult.undo();
          clientSubscriptionPatch?.undo();
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage:
              errorMessage || 'Subscription could not be cancelled.',
            error,
            dispatch,
          });
        }
      },
    }),
    getSubscriptionTemplates: build.query<SubscriptionTemplate[], void>({
      query: () => ({
        path: '/entities/stripe_subscription',
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            isTemplateQuery: true,
          },
        },
      }),
      providesTags: [ApiTags.templates],
    }),
    createSubscriptionTemplate: build.mutation<
      SubscriptionTemplate,
      SubscriptionTemplateSchema
    >({
      query: (data) => {
        const { fields } = data;
        return {
          path: `/subscription-templates`,
          method: ApiMethods.post,
          options: {
            body: {
              fields: {
                ...fields,
                lineItems: fields.lineItems.map((lineItem) => ({
                  ...lineItem,
                  id: '',
                  rate: Number(lineItem.amount),
                  quantity: Number(lineItem.quantity),
                })),
                taxPercentage: Number(fields.taxPercentage),
              },
              additionalFields: {
                templateName: data.additionalFields.templateName,
              },
            },
          },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          dispatch(
            subscriptionsApi.util.updateQueryData(
              'getSubscriptionTemplates',
              undefined,
              (draft) => {
                draft.push(result.data);
              },
            ),
          );
          notify({
            status: 'success',
            successMessage: 'Template is saved and available for future use.',
            dispatch,
          });
          history.push(SUBSCRIPTION_PAGE.path);
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error?.message;
          notify({
            status: 'error',
            errorMessage:
              errorMessage || 'Subscription template could not be created.',
            error,
            dispatch,
          });
        }
      },
    }),
    updateSubscriptionTemplate: build.mutation<
      SubscriptionTemplate,
      UpdateSubscriptionTemplatePayload
    >({
      query: (data) => {
        const { fields, id } = data;

        return {
          path: `/subscription-templates/${id}`,
          method: ApiMethods.put,
          options: {
            body: {
              fields: {
                ...fields,
                lineItems: fields.lineItems.map((lineItem) => ({
                  ...lineItem,
                  id: '',
                  rate: Number(lineItem.amount),
                  quantity: Number(lineItem.quantity),
                })),
                taxPercentage: Number(fields.taxPercentage),
              },
              additionalFields: {
                templateName: data.additionalFields.templateName,
              },
            },
          },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          dispatch(
            subscriptionsApi.util.updateQueryData(
              'getSubscriptionTemplates',
              undefined,
              (draft) =>
                draft.map((template) => {
                  if (template.id === result.data.id) {
                    return result.data;
                  }

                  return template;
                }),
            ),
          );
          notify({
            status: 'success',
            successMessage: 'Template is saved and available for future use.',
            dispatch,
          });
          history.push(SUBSCRIPTION_PAGE.path);
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage:
              errorMessage || 'This Subscription Template could not be saved.',
            error,
            dispatch,
          });
        }
      },
    }),
  }),
});

const cancelSubscriptionInCache = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  queryName: 'getSubscriptionsByRecipientId' | 'getSubscriptions',
  queryParameter: GetSubscriptionsByRecipientIdQueryParams | string | undefined,
  subscriptionId: string,
) => {
  return dispatch(
    subscriptionsApi.util.updateQueryData(
      queryName,
      queryParameter,
      (draft: Subscription[]) => {
        const index = draft.findIndex((item) => item.id === subscriptionId);
        if (index !== -1) {
          draft[index] = {
            ...draft[index],
            additionalFields: {
              ...draft[index].additionalFields,
              status: SubscriptionStatus.Canceled,
            },
          };
        }

        // Re-sort the array
        draft.sort((a, b) => {
          // First sort by status (Active first, then Cancelled)
          if (a.additionalFields?.status !== b.additionalFields?.status) {
            return a.additionalFields?.status === SubscriptionStatus.Active
              ? -1
              : 1;
          }
          // Then sort by creation date (newest first)
          return b.createdAt.localeCompare(a.createdAt);
        });
      },
    ),
  );
};

const deleteSubscriptionFromCache = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  queryName: 'getSubscriptionsByRecipientId' | 'getSubscriptions',
  queryParameter: GetSubscriptionsByRecipientIdQueryParams | string | undefined,
  subscriptionId: string,
) => {
  return dispatch(
    subscriptionsApi.util.updateQueryData(queryName, queryParameter, (draft) =>
      draft.filter((i) => i.id !== subscriptionId),
    ),
  );
};

const updateSubscriptionQueryOptimistically = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  queryName: 'getSubscriptionsByRecipientId' | 'getSubscriptions',
  queryParameter: GetSubscriptionsByRecipientIdQueryParams | string | undefined,
  subscription: Subscription,
) => {
  return dispatch(
    subscriptionsApi.util.updateQueryData(
      queryName,
      queryParameter,
      (draft: Subscription[]) => {
        const index = draft.findIndex((item) => item.id === subscription.id);
        if (index === -1) {
          draft.unshift(subscription);
        } else {
          draft[index] = subscription;
        }
      },
    ),
  );
};

export const updateSubscriptionQuery = (
  subscription: Subscription,
  dispatch: ThunkDispatch<any, any, AnyAction>,
  state: RootState,
) => {
  const { clientUserId, companyId } = subscription.fields;
  const { companies, clients } = state.clients;
  if (companyId) {
    const companiesMap = arrayToMap(companies, 'id');
    // If found, this indicates that this is a company subscription
    const company = companiesMap.get(companyId);

    updateSubscriptionQueryOptimistically(
      dispatch,
      'getSubscriptionsByRecipientId',
      companyId,
      subscription,
    );

    if (company) {
      // For company subscription, we need to update the client details page for all client for this company
      const companyClientsIds = clients
        .filter((client) => client.fields.companyId === companyId)
        .map((client) => client.id);
      for (const clientId of companyClientsIds) {
        updateSubscriptionQueryOptimistically(
          dispatch,
          'getSubscriptionsByRecipientId',
          clientId,
          subscription,
        );
      }
    }
  }
  // Update the client details subscription list for that particular client
  if (clientUserId) {
    updateSubscriptionQueryOptimistically(
      dispatch,
      'getSubscriptionsByRecipientId',
      clientUserId,
      subscription,
    );
  }

  updateSubscriptionQueryOptimistically(
    dispatch,
    'getSubscriptions',
    undefined,
    subscription,
  );
};

export const {
  useGetSubscriptionsQuery,
  useGetSubscriptionEntityByIdQuery,
  useGetNextInvoiceSummaryQuery,
  useMarkSubscriptionDeletedMutation,
  useGetSubscriptionByIdQuery,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useGetSubscriptionTemplatesQuery,
  useCreateSubscriptionTemplateMutation,
  useUpdateSubscriptionTemplateMutation,
  useDeleteSubscriptionMutation,
  useCancelSubscriptionMutation,
  useGetSubscriptionsByRecipientIdQuery,
} = subscriptionsApi;
